export type RoundingMode = "strict" | "round";

export type ConversionSuccess = {
  ok: true;
  output: string;
  normalized: string;
  integer: string;
  cents: number;
  isNegative: boolean;
};

export type ConversionError = {
  ok: false;
  code: "empty" | "format" | "precision" | "range";
  message: string;
};

export type ConversionResult = ConversionSuccess | ConversionError;

export type ConvertOptions = {
  prefix?: boolean;
  rounding?: RoundingMode;
};

const DIGITS = ["零", "壹", "贰", "叁", "肆", "伍", "陆", "柒", "捌", "玖"];
const INNER_UNITS = ["", "拾", "佰", "仟"];
const GROUP_UNITS = ["", "万", "亿", "兆", "京"];
const MAX_INTEGER_DIGITS = GROUP_UNITS.length * 4;

export function convertRmbToUppercase(
  raw: string,
  options: ConvertOptions = {}
): ConversionResult {
  const prefix = options.prefix ?? true;
  const rounding = options.rounding ?? "strict";
  const parsed = parseAmount(raw, rounding);

  if (!parsed.ok) {
    return parsed;
  }

  const body = formatRmbBody(parsed.integer, parsed.cents);
  const negativePrefix = parsed.isNegative ? "负" : "";
  const currencyPrefix = prefix ? "人民币" : "";

  return {
    ...parsed,
    output: `${negativePrefix}${currencyPrefix}${body}`
  };
}

export function convertIntegerToUppercase(integer: string): string {
  const normalized = stripLeadingZeros(integer);

  if (normalized === "0") {
    return DIGITS[0];
  }

  if (normalized.length > MAX_INTEGER_DIGITS) {
    throw new RangeError(`整数部分最多支持 ${MAX_INTEGER_DIGITS} 位。`);
  }

  const groups = splitIntoGroups(normalized);
  let result = "";
  let needsBoundaryZero = false;

  groups.forEach((group, index) => {
    const unitIndex = groups.length - index - 1;
    const groupValue = Number(group);

    if (groupValue === 0) {
      needsBoundaryZero = result.length > 0;
      return;
    }

    if (
      result.length > 0 &&
      (needsBoundaryZero || group.length < 4 || group.startsWith("0"))
    ) {
      result += DIGITS[0];
    }

    result += `${convertGroup(group)}${GROUP_UNITS[unitIndex]}`;
    needsBoundaryZero = false;
  });

  return result;
}

function parseAmount(raw: string, rounding: RoundingMode): ConversionResult {
  const cleaned = normalizeRawInput(raw);

  if (!cleaned || cleaned === "-" || cleaned === "+" || cleaned === ".") {
    return {
      ok: false,
      code: "empty",
      message: "输入金额"
    };
  }

  if (!/^[+-]?\d*(?:\.\d*)?$/.test(cleaned) || !/\d/.test(cleaned)) {
    return {
      ok: false,
      code: "format",
      message: "金额格式需要是数字"
    };
  }

  const isNegative = cleaned.startsWith("-");
  const unsigned = cleaned.replace(/^[+-]/, "");
  const [integerRaw = "0", fractionRaw = ""] = unsigned.split(".");

  if (rounding === "strict" && fractionRaw.length > 2) {
    return {
      ok: false,
      code: "precision",
      message: "小数最多保留到分"
    };
  }

  const rounded = rounding === "round"
    ? roundAmount(integerRaw || "0", fractionRaw)
    : {
        integer: stripLeadingZeros(integerRaw || "0"),
        cents: Number((fractionRaw || "").padEnd(2, "0").slice(0, 2))
      };

  if (rounded.integer.length > MAX_INTEGER_DIGITS) {
    return {
      ok: false,
      code: "range",
      message: `整数部分最多 ${MAX_INTEGER_DIGITS} 位`
    };
  }

  const isZero = rounded.integer === "0" && rounded.cents === 0;
  const normalized = `${isNegative && !isZero ? "-" : ""}${formatNumber(
    rounded.integer,
    rounded.cents
  )}`;

  return {
    ok: true,
    integer: rounded.integer,
    cents: rounded.cents,
    normalized,
    isNegative: isNegative && !isZero,
    output: ""
  };
}

function normalizeRawInput(raw: string): string {
  return raw
    .trim()
    .replace(/[０-９]/g, (char) =>
      String.fromCharCode(char.charCodeAt(0) - 0xfee0)
    )
    .replace(/[＋]/g, "+")
    .replace(/[－﹣−]/g, "-")
    .replace(/[．。]/g, ".")
    .replace(/人民币|圆|元|￥|¥|RMB|rmb|CNY|cny/g, "")
    .replace(/[,\s，]/g, "");
}

function roundAmount(
  integerRaw: string,
  fractionRaw: string
): { integer: string; cents: number } {
  const integer = stripLeadingZeros(integerRaw || "0");
  const firstTwo = fractionRaw.padEnd(2, "0").slice(0, 2);
  const shouldRoundUp = Number(fractionRaw[2] ?? "0") >= 5;
  const centsTotal =
    BigInt(integer) * 100n + BigInt(firstTwo || "0") + (shouldRoundUp ? 1n : 0n);

  return {
    integer: String(centsTotal / 100n),
    cents: Number(centsTotal % 100n)
  };
}

function formatRmbBody(integer: string, cents: number): string {
  const integerText = convertIntegerToUppercase(integer);
  const jiao = Math.floor(cents / 10);
  const fen = cents % 10;

  if (cents === 0) {
    return `${integerText}元整`;
  }

  if (jiao > 0 && fen > 0) {
    return `${integerText}元${DIGITS[jiao]}角${DIGITS[fen]}分`;
  }

  if (jiao > 0) {
    return `${integerText}元${DIGITS[jiao]}角`;
  }

  return `${integerText}元${integer === "0" ? "" : "零"}${DIGITS[fen]}分`;
}

function convertGroup(group: string): string {
  let result = "";
  let pendingZero = false;

  Array.from(group).forEach((char, index) => {
    const digit = Number(char);
    const unitIndex = group.length - index - 1;

    if (digit === 0) {
      pendingZero = result.length > 0;
      return;
    }

    if (pendingZero) {
      result += DIGITS[0];
    }

    result += `${DIGITS[digit]}${INNER_UNITS[unitIndex]}`;
    pendingZero = false;
  });

  return result;
}

function splitIntoGroups(value: string): string[] {
  const groups: string[] = [];

  for (let end = value.length; end > 0; end -= 4) {
    groups.unshift(value.slice(Math.max(0, end - 4), end));
  }

  return groups;
}

function stripLeadingZeros(value: string): string {
  return value.replace(/^0+(?=\d)/, "") || "0";
}

function formatNumber(integer: string, cents: number): string {
  return `${addThousands(integer)}.${String(cents).padStart(2, "0")}`;
}

function addThousands(value: string): string {
  return value.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}
